import FieldsConstants from 'src/app/common/fields.constants';
import FieldAllowedAttributeTypes from 'src/app/forms/fields/field-allowed-attribute-types.values';
import FieldAllowedValueClasses from 'src/app/forms/fields/field-allowed-value-classes.values';
import { FormTypes } from 'src/app/forms/forms.enum';
import { Attribute } from 'src/app/interfaces/form-and-field.interface';
import { LocationObject, OrganizationLink, Viewport } from 'src/app/swagger-api';

export interface ProjectForm {
  title: string;
  description: string;
  start_date: string;
  end_date: string;
  website: string;
  type: string;
  diseases: string;
  funding_source: string;
  specialities: string;
  linked_organizations: OrganizationLink[];
  location: LocationObject;
  viewport: Viewport;
  community_id: string;
  linked_projects: string;
  accept_terms: boolean;
}

export const linkedOrganizationsInternalName = 'linked_organizations';

export const projectOrganizationMetadata: Attribute = {
  name: linkedOrganizationsInternalName,
  value_class_front: FieldAllowedValueClasses.LINKED_ORGANIZATION_IDS_SELECT,
  min_length: 1,
  max_length: 100,
  type_attribute: FieldAllowedAttributeTypes.MULTIPLE_SUGGESTIONS_SEARCH_SELECT,
  required: true,
  read_only: false,
  label: 'FORM_CONSTRUCTOR_PROJECT_ORGANIZATIONS_LABEL',
  placeholder: 'FORM_CONSTRUCTOR_PROJECT_ORGANIZATIONS_PLACEHOLDER',
  security: false,
  scheme_name: FormTypes.PROJECT,
};

export const projectSpecialitiesMetadata: Attribute = {
  name: 'specialities',
  value_class_front: FieldAllowedValueClasses.SPECIALTIES_SELECT,
  min_length: 1,
  max_length: 2000,
  type_attribute: FieldAllowedAttributeTypes.MULTIPLE_CUSTOM_SELECT,
  required: true,
  read_only: false,
  label: 'FORM_CONSTRUCTOR_PROJECT_SPECIALTIES_LABEL',
  placeholder: 'FORM_CONSTRUCTOR_PROJECT_SPECIALTIES_PLACEHOLDER',
  security: false,
  scheme_name: FormTypes.PROJECT,
};

export const projectDiseasesMetadata: Attribute = {
  name: 'diseases',
  value_class_front: FieldAllowedValueClasses.DISEASES_SELECT,
  min_length: 1,
  max_length: 2000,
  type_attribute: FieldAllowedAttributeTypes.MULTIPLE_CUSTOM_SELECT,
  required: true,
  read_only: false,
  label: 'FORM_CONSTRUCTOR_PROJECT_DISEASES_LABEL',
  placeholder: 'FORM_CONSTRUCTOR_PROJECT_DISEASES_PLACEHOLDER',
  security: false,
  scheme_name: FormTypes.PROJECT,
};

export const projectTitleMetadata: Attribute = {
  name: 'title',
  value_class_front: FieldAllowedValueClasses.CHARACTERS,
  min_length: 1,
  max_length: 50,
  type_attribute: FieldAllowedAttributeTypes.TEXT,
  required: true,
  read_only: false,
  label: 'FORM_CONSTRUCTOR_PROJECT_TITLE_LABEL',
  placeholder: 'FORM_CONSTRUCTOR_PROJECT_TITLE_PLACEHOLDER',
  security: false,
  scheme_name: FormTypes.PROJECT,
};

export const projectLinkedProjectsMetadata: Attribute = {
  name: 'linked_projects',
  value_class_front: FieldAllowedValueClasses.LINKED_PROJECT_IDS_SELECT,
  min_length: 1,
  max_length: 2000,
  type_attribute: FieldAllowedAttributeTypes.MULTIPLE_SUGGESTIONS_SEARCH_SELECT,
  required: false,
  read_only: false,
  label: 'FORM_CONSTRUCTOR_PROJECT_LINKED_PROJECTS_LABEL',
  placeholder: 'FORM_CONSTRUCTOR_PROJECT_LINKED_PROJECTS_PLACEHOLDER',
  security: false,
  scheme_name: FormTypes.PROJECT,
};

export const projectDescriptionMetadata: Attribute = {
  name: 'description',
  value_class_front: FieldAllowedValueClasses.TEXTAREA_FULL,
  min_length: 1,
  max_length: 1000,
  type_attribute: FieldAllowedAttributeTypes.TEXTAREA,
  required: true,
  read_only: false,
  label: 'FORM_CONSTRUCTOR_PROJECT_DESCRIPTION_LABEL',
  placeholder: 'FORM_CONSTRUCTOR_PROJECT_DESCRIPTION_PLACEHOLDER',
  security: false,
  scheme_name: FormTypes.PROJECT,
};

export const projectStartDateMetadata: Attribute = {
  name: 'start_date',
  value_class_front: FieldAllowedValueClasses.DATE,
  min_length: 1,
  max_length: 50,
  type_attribute: FieldAllowedAttributeTypes.TEXT,
  required: true,
  read_only: false,
  label: 'FORM_CONSTRUCTOR_PROJECT_START_DATE_LABEL',
  placeholder: 'FORM_CONSTRUCTOR_PROJECT_START_DATE_PLACEHOLDER',
  security: false,
  enabled_if: { name: 'end_date', value: null },
  scheme_name: FormTypes.PROJECT,
};

export const projectEndDateMetadata: Attribute = {
  name: 'end_date',
  value_class_front: FieldAllowedValueClasses.DATE,
  min_length: 1,
  max_length: 50,
  type_attribute: FieldAllowedAttributeTypes.TEXT,
  required: true,
  read_only: false,
  label: 'FORM_CONSTRUCTOR_PROJECT_END_DATE_LABEL',
  placeholder: 'FORM_CONSTRUCTOR_PROJECT_END_DATE_PLACEHOLDER',
  security: false,
  scheme_name: FormTypes.PROJECT,
  enabled_if: { name: 'start_date', value: null },
};

export const projectWebsiteMetadata: Attribute = {
  name: 'website',
  value_class_front: FieldAllowedValueClasses.URL,
  min_length: FieldsConstants.MIN_WEBSITE_LINK_LENGTH,
  max_length: FieldsConstants.MAX_WEBSITE_LINK_LENGTH,
  type_attribute: FieldAllowedAttributeTypes.TEXT,
  required: false,
  read_only: false,
  label: 'FORM_CONSTRUCTOR_PROJECT_WEBSITE_LABEL',
  placeholder: 'FORM_CONSTRUCTOR_PROJECT_WEBSITE_PLACEHOLDER',
  security: false,
  scheme_name: FormTypes.PROJECT,
};

export const projectTypeMetadata: Attribute = {
  name: 'type',
  value_class_front: FieldAllowedValueClasses.CHARACTERS,
  min_length: 1,
  max_length: 50,
  type_attribute: FieldAllowedAttributeTypes.TEXT,
  required: true,
  read_only: false,
  label: 'FORM_CONSTRUCTOR_PROJECT_TYPE_LABEL',
  placeholder: 'FORM_CONSTRUCTOR_PROJECT_TYPE_PLACEHOLDER',
  security: true,
  scheme_name: FormTypes.PROJECT,
};

export const projectFundingSourceMetadata: Attribute = {
  name: 'funding_source',
  value_class_front: FieldAllowedValueClasses.TEXTAREA_FULL,
  min_length: 1,
  max_length: 200,
  type_attribute: FieldAllowedAttributeTypes.TEXTAREA,
  required: false,
  read_only: false,
  label: 'FORM_CONSTRUCTOR_PROJECT_FUNDING_SOURCE_LABEL',
  placeholder: 'FORM_CONSTRUCTOR_PROJECT_FUNDING_SOURCE_PLACEHOLDER',
  security: false,
  scheme_name: FormTypes.PROJECT,
};

export const projectLocationMetadata: Attribute = {
  name: 'location',
  value_class_front: FieldAllowedValueClasses.CITY_LEVEL_LOCATION,
  min_length: 1,
  max_length: 50,
  type_attribute: FieldAllowedAttributeTypes.TEXT,
  required: true,
  read_only: false,
  label: 'FORM_CONSTRUCTOR_PROJECT_LOCATION_LABEL',
  placeholder: 'FORM_CONSTRUCTOR_PROJECT_LOCATION_PLACEHOLDER',
  security: false,
  scheme_name: FormTypes.PROJECT,
};

export const projectViewportFieldMetadata: Attribute = {
  name: 'viewport',
  value_class_front: FieldAllowedValueClasses.VIEWPORT,
  min_length: 1,
  max_length: 50,
  type_attribute: FieldAllowedAttributeTypes.TEXT,
  required: false,
  read_only: true,
  label: 'Viewport',
  placeholder: 'Automatically by viewport',
  security: false,
  scheme_name: FormTypes.PROJECT,
};

export const projectCommunityIdMetadata: Attribute = {
  name: 'community_id',
  value_class_front: FieldAllowedValueClasses.CONNECT_COMMUNITY_SEARCH_SELECT,
  min_length: 1,
  max_length: 100,
  type_attribute: FieldAllowedAttributeTypes.SEARCH_SELECT,
  required: false,
  counter_hide: true,
  read_only: false,
  label: 'FORM_CONSTRUCTOR_PROJECT_COMMUNITY_LABEL',
  placeholder: 'FORM_CONSTRUCTOR_PROJECT_COMMUNITY_PLACEHOLDER',
  security: false,
  scheme_name: FormTypes.PROJECT,
};

export const projectAcceptTermsMetadata: Attribute = {
  name: 'accept_terms',
  value_class_front: FieldAllowedValueClasses.TERMS_CHECKBOX,
  min_length: 1,
  max_length: 50,
  type_attribute: FieldAllowedAttributeTypes.CHECKBOX,
  required: true,
  read_only: false,
  label: 'FORM_CONSTRUCTOR_PROJECT_TERMS_LABEL',
  placeholder: 'FORM_CONSTRUCTOR_PROJECT_TERMS_PLACEHOLDER',
  security: false,
  scheme_name: FormTypes.PROJECT,
};

export const projectFormMetadata: Attribute[] = [
  projectTitleMetadata,
  projectDescriptionMetadata,
  projectStartDateMetadata,
  projectEndDateMetadata,
  projectWebsiteMetadata,
  projectTypeMetadata,
  projectDiseasesMetadata,
  projectFundingSourceMetadata,
  projectSpecialitiesMetadata,
  projectOrganizationMetadata,
  projectLocationMetadata,
  projectViewportFieldMetadata,
  projectCommunityIdMetadata,
  projectLinkedProjectsMetadata,
  projectAcceptTermsMetadata,
];
